import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle, Phone } from "lucide-react";
import { BRAND, PHOTOS, UI } from "@/content";
import { useLang } from "@/LangContext";

export default function Hero() {
  const { lang } = useLang();
  const t = UI[lang] || UI.es;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1.14]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col justify-center items-center text-center overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={PHOTOS.room}
          alt="Sala de tratamiento Royale"
          className="w-full h-full object-cover"
          data-testid="hero-photo"
        />
        <div className="absolute inset-0 bg-forest/65" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/50 via-transparent to-forest" />
      </motion.div>

      <div className="candle-glow absolute top-1/4 right-[15%] w-64 h-64 rounded-full bg-gold/15 blur-[110px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-4xl mx-auto px-6 pt-28 pb-20"
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="font-mono text-[10px] sm:text-xs tracking-[0.32em] uppercase text-goldbright mb-7 flex items-center justify-center gap-3"
        >
          <span className="h-px w-10 bg-gold/70 inline-block" />
          {t.hero.eyebrow}
          <span className="h-px w-10 bg-gold/70 inline-block" />
        </motion.p>

        <h1 className="overflow-hidden">
          <motion.span
            className="block font-serif font-medium text-cream text-[clamp(4rem,14vw,9.5rem)] leading-[0.95] tracking-[0.18em] -mr-[0.18em]"
            initial={{ y: "108%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.2, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            {t.hero.title}
          </motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="font-serif italic text-gold text-2xl sm:text-3xl mt-5 tracking-wide"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-cream/75 mt-8"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.82 }}
          className="mt-5 max-w-xl mx-auto text-cream/85 text-base sm:text-lg leading-relaxed font-light"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href={BRAND.fresha}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-fresha"
            className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-[15px] font-medium text-forest hover:bg-goldbright hover:scale-[1.03] active:scale-[0.98] transition-transform duration-300"
          >
            {t.hero.book}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp"
            className="inline-flex items-center gap-2.5 rounded-full border border-cream/40 bg-forest/30 backdrop-blur px-7 py-4 text-[15px] font-medium text-cream hover:bg-cream/10 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
          >
            <MessageCircle className="w-[18px] h-[18px]" />
            {t.hero.whatsapp}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
        >
          <a
            href={BRAND.phoneHref}
            data-testid="hero-phone-chip"
            className="inline-flex items-center gap-2.5 text-sm text-cream/85 hover:text-goldbright transition-colors duration-300"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full border border-gold/40">
              <Phone className="w-3.5 h-3.5 text-gold" />
            </span>
            {t.hero.ask}: <span className="font-medium whitespace-nowrap">{BRAND.phoneDisplay}</span>
          </a>
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-cream/60">
            {t.hero.hours}
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-cream/60">
          {t.hero.scroll}
        </span>
        <span className="w-px h-10 bg-gradient-to-b from-gold/80 to-transparent" />
      </motion.div>
    </section>
  );
}
